import {
  isPolicyPayloadV1,
  isPolicyTreeBundleV1,
  isPolicyTreeV2,
  type PolicyTreeBundleSegmentV1,
  type PolicyTreeBundleV1,
  type PolicyTreeV2,
  type PolicyTreeV2Rule,
  type TradePolicyAction,
  type TradePolicyCatalogField,
  type TradePolicyGroupNode,
  type TradePolicyGroupOp,
  type TradePolicyPolicyNode,
  type TradePolicyPolicyPayload,
  type TradePolicyRuleNode,
  type TradePolicyRuleOp,
  type TradePolicySimulatePolicy,
} from "../../../types/tradePolicy";
import { isPlainObject, unwrapRedundantPolicyGroup, versionLabel } from "../Strategies/strategyDetailUtils";
import type { ReportStrategyPolicyVersion } from "../../../types/reports";
import {
  normalizeCandleTimeframe,
  resolveFinestCandleTimeframe,
} from "../../../utils/candleTimeframeBarBudget";
import { candleTimeframeFromPolicyTree } from "../../../utils/dualCandleTimeframe";
import { policyCatalogFieldDisplayLabel } from "../../../utils/policyCatalogFieldLabels";

/** User-facing policy action label (Remove / Keep). */
export function formatPolicyActionLabel(action: unknown): string | null {
  const raw = typeof action === "string" ? action.trim().toLowerCase() : "";
  if (raw === "veto") return "Remove";
  if (raw === "keep_only" || raw === "keep-only" || raw === "keep only") return "Keep";
  return null;
}

export function extractPolicyAction(raw: unknown): string | null {
  if (!isPlainObject(raw)) return null;
  return formatPolicyActionLabel(raw.action);
}

function asTradePolicyAction(raw: unknown): TradePolicyAction | null {
  return raw === "veto" || raw === "keep_only" ? raw : null;
}

/**
 * Default simulator action from a classic / bundle policy tree.
 * Prefers top-level `action`; for bundles uses a uniform segment action.
 * Returns null when segment actions differ (mixed Remove + Keep) — do not invent one.
 */
export function resolvePolicyDefaultSimAction(raw: unknown): TradePolicyAction | null {
  if (!isPlainObject(raw)) return null;
  const top = asTradePolicyAction(raw.action);
  if (top) return top;
  const actions = collectPolicySegmentActions(raw);
  if (actions.length === 0) return null;
  if (actions.every((a) => a === actions[0])) return actions[0]!;
  return null;
}

/** True when a bundle has both Remove and Keep (or otherwise disagreeing) segment actions. */
export function isMixedPolicySegmentActions(raw: unknown): boolean {
  const actions = collectPolicySegmentActions(raw);
  if (actions.length < 2) return false;
  return !actions.every((a) => a === actions[0]);
}

function collectPolicySegmentActions(raw: unknown): TradePolicyAction[] {
  if (!isPlainObject(raw)) return [];
  const segmentsRaw = raw.segments;
  if (!Array.isArray(segmentsRaw) || segmentsRaw.length === 0) return [];
  return segmentsRaw
    .map((segment) => (isPlainObject(segment) ? asTradePolicyAction(segment.action) : null))
    .filter((a): a is TradePolicyAction => a != null);
}
import { humanizeManagementAction } from "../../../utils/managementActionLabels";

export { isPolicyPayloadV1, isPolicyTreeBundleV1, isPolicyTreeV2 };

const RULE_OP_SYMBOL: Record<string, string> = {
  eq: "=",
  ne: "≠",
  in: "one of",
  gte: "≥",
  lte: "≤",
};

/** Sentinel enum tokens from the filter catalog — valid on trades but not useful in manual rule building. */
const HIDDEN_CATALOG_ENUM_PICKER_VALUES = new Set(["unknown", "none"]);

/**
 * Enum tokens shown in simulator value pickers. Hides unclassified sentinels (`unknown`, `none`)
 * unless already selected on the rule (so loaded policies still display correctly).
 */
export function catalogEnumValuesForPicker(
  enumValues: string[] | undefined,
  retainTokens: string[] = []
): string[] {
  const retain = new Set(retainTokens.map((t) => t.trim()).filter(Boolean));
  return (enumValues ?? []).filter((token) => {
    const t = token.trim();
    if (!t) return false;
    if (HIDDEN_CATALOG_ENUM_PICKER_VALUES.has(t.toLowerCase())) {
      return retain.has(token);
    }
    return true;
  });
}

function formatSerializedRuleValue(rule: TradePolicyRuleNode): string {
  const v = rule.value;
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "number") return String(v);
  return String(v);
}

export const GROUP_POLICY_ACTION_OPTIONS: { value: TradePolicyAction; label: string }[] = [
  { value: "veto", label: "Remove" },
  { value: "keep_only", label: "Keep Only" },
];

/**
 * Human-readable summary of a serialized policy (for comparison cards / snapshots).
 */
export function formatPolicyPayloadSummary(
  payload: TradePolicyPolicyPayload,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): string {
  const actionLine =
    payload.action === "veto"
      ? "Action: Remove — trades matching the tree are removed from the filtered set"
      : "Action: Keep — trades not matching the tree are removed from the filtered set";
  const lines = [actionLine, "", ...formatPolicyNodeSummaryLines(payload.root, fieldsByPath, 0)];
  return lines.join("\n");
}

function formatPolicyTreeV2Summary(
  policy: PolicyTreeV2,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): string {
  const rules = policy.rules ?? [];
  if (rules.length === 0) {
    return "Exit placement policy (policy_tree.v2)\n\nNo rules defined.";
  }

  const lines: string[] = ["Exit placement policy (policy_tree.v2):", ""];
  rules.forEach((rule, index) => {
    const enabled = rule.enabled !== false;
    const idSuffix = rule.id?.trim() ? ` (${rule.id.trim()})` : "";
    lines.push(`Rule ${index + 1}${idSuffix}${enabled ? "" : " [disabled]"}`);
    lines.push("  WHEN:");
    const condLines = formatPolicyNodeSummaryLines(rule.condition, fieldsByPath, 2);
    lines.push(...(condLines.length ? condLines : ["    (no condition)"]));
    lines.push("  THEN:");
    const actions = rule.actions ?? [];
    if (actions.length === 0) {
      lines.push("    (no actions)");
    } else {
      for (const action of actions) {
        lines.push(`    • ${humanizeManagementAction(action)}`);
      }
    }
    lines.push("");
  });
  return lines.join("\n").trim();
}

/** Summary for v1 payloads, mixed-action bundles, or policy_tree.v2 exit rules. */
export function formatSimulatePolicySummary(
  policy: TradePolicySimulatePolicy,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): string {
  if (isPolicyTreeBundleV1(policy)) {
    const lines: string[] = ["Policy bundle (mixed actions):", ""];
    policy.segments.forEach((seg, i) => {
      const actionLine =
        seg.action === "veto"
          ? "Remove — trades matching this segment are removed"
          : "Keep only — trades not matching this segment are removed";
      const title = seg.source_title?.trim();
      lines.push(`Segment ${i + 1}${title ? ` (${title})` : ""}: ${actionLine}`);
      lines.push(...formatPolicyNodeSummaryLines(seg.root, fieldsByPath, 1));
      lines.push("");
    });
    return lines.join("\n").trim();
  }
  if (isPolicyTreeV2(policy)) {
    return formatPolicyTreeV2Summary(policy, fieldsByPath);
  }
  if (isPolicyPayloadV1(policy)) {
    return formatPolicyPayloadSummary(policy, fieldsByPath);
  }
  return "Policy summary unavailable for this schema.";
}

const NATURAL_RULE_OP: Record<string, string> = {
  eq: "is",
  ne: "is not",
  in: "is one of",
  gte: "is at least",
  lte: "is at most",
};

export type PolicySummaryPart =
  | { kind: "plain"; text: string }
  | { kind: "condition"; text: string };

export type PolicySummaryParagraph = PolicySummaryPart[];

export type PolicyViewSummaryDocument = PolicySummaryParagraph[];

function joinNaturalLanguageList(parts: string[], conjunction: "and" | "or"): string {
  const cleaned = parts.map((part) => part.trim()).filter(Boolean);
  if (cleaned.length === 0) return "";
  if (cleaned.length === 1) return cleaned[0];
  if (cleaned.length === 2) return `${cleaned[0]} ${conjunction} ${cleaned[1]}`;
  return `${cleaned.slice(0, -1).join(", ")}, ${conjunction} ${cleaned[cleaned.length - 1]}`;
}

function formatRuleValueNatural(rule: TradePolicyRuleNode): string {
  const value = rule.value;
  if (Array.isArray(value)) {
    if (value.length === 0) return "nothing selected";
    return joinNaturalLanguageList(value.map(String), "or");
  }
  if (typeof value === "number") return String(value);
  const text = String(value ?? "").trim();
  return text || "any value";
}

function formatSingleRulePhrase(
  rule: TradePolicyRuleNode,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): string {
  const rawLabel = fieldsByPath.get(rule.path)?.label ?? rule.path;
  const label = policyCatalogFieldDisplayLabel(rule.path, rawLabel);
  const op = NATURAL_RULE_OP[rule.op] ?? rule.op;
  return `${label} ${op} ${formatRuleValueNatural(rule)}`;
}

/** Flatten AND groups into individual rule phrases; keep OR siblings separate when possible. */
function collectConditionPhrases(
  node: TradePolicyPolicyNode,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): string[] {
  if (node.kind === "rule") {
    return [formatSingleRulePhrase(node, fieldsByPath)];
  }

  const childPhrases = node.children.flatMap((child) =>
    collectConditionPhrases(child, fieldsByPath)
  );
  if (childPhrases.length === 0) return [];

  if (node.op === "or") {
    const directRules = node.children.every((child) => child.kind === "rule");
    if (directRules) return childPhrases;
    return [joinNaturalLanguageList(childPhrases, "or")];
  }

  return childPhrases;
}

function joinConditionParts(
  phrases: string[],
  conjunction: "and" | "or" = "and"
): PolicySummaryPart[] {
  if (phrases.length === 0) return [];

  const parts: PolicySummaryPart[] = [{ kind: "condition", text: phrases[0] }];
  for (let index = 1; index < phrases.length; index += 1) {
    const isLast = index === phrases.length - 1;
    const separator =
      phrases.length === 2
        ? ` ${conjunction} `
        : isLast
          ? `, ${conjunction} `
          : ", ";
    parts.push({ kind: "plain", text: separator });
    parts.push({ kind: "condition", text: phrases[index] });
  }
  return parts;
}

function resolvePayloadRoot(payload: unknown): TradePolicyPolicyNode | null {
  if (!isPlainObject(payload)) return null;
  const record = payload as Record<string, unknown>;
  let rootCandidate: unknown = "root" in record ? record.root : payload;
  if (isPlainObject(rootCandidate)) {
    rootCandidate = unwrapRedundantPolicyGroup(rootCandidate);
  }
  if (!isPolicyNode(rootCandidate)) return null;
  return rootCandidate as TradePolicyPolicyNode;
}

function plainParagraph(text: string): PolicySummaryParagraph {
  return [{ kind: "plain", text }];
}

function buildFilterSegmentSummaryParagraph(
  payload: unknown,
  fieldsByPath: Map<string, TradePolicyCatalogField>,
  opts?: { groupIndex?: number; groupCount?: number; groupName?: string | null }
): PolicySummaryParagraph {
  const action = extractPolicyAction(payload);
  const root = resolvePayloadRoot(payload);
  const phrases = root ? collectConditionPhrases(root, fieldsByPath) : [];
  const isMultiGroup =
    typeof opts?.groupCount === "number" && opts.groupCount > 1;
  const parts: PolicySummaryPart[] = [];

  if (isMultiGroup && typeof opts?.groupIndex === "number") {
    const groupName = opts.groupName?.trim() ?? "";
    const label = groupName
      ? `Group ${opts.groupIndex + 1} (${groupName}): `
      : `Group ${opts.groupIndex + 1}: `;
    parts.push({ kind: "plain", text: label });
  } else {
    parts.push({ kind: "plain", text: "This policy " });
  }

  if (phrases.length === 0) {
    if (action === "Remove") {
      parts.push({ kind: "plain", text: "removes trades, but no conditions are defined yet." });
    } else if (action === "Keep") {
      parts.push({
        kind: "plain",
        text: "keeps only trades that match your rules, but no conditions are defined yet.",
      });
    } else {
      parts.push({ kind: "plain", text: "does not have any conditions defined yet." });
    }
    return parts;
  }

  const actionLead =
    action === "Remove"
      ? isMultiGroup
        ? "Removes trades when "
        : "removes trades when "
      : action === "Keep"
        ? isMultiGroup
          ? "Keeps only trades when "
          : "keeps only trades when "
        : isMultiGroup
          ? "Filters trades when "
          : "filters trades when ";

  parts.push({ kind: "plain", text: actionLead });
  parts.push(...joinConditionParts(phrases, "and"));
  parts.push({ kind: "plain", text: "." });
  return parts;
}

function buildManagementRuleSummaryParagraph(
  rule: PolicyTreeV2Rule,
  index: number,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): PolicySummaryParagraph {
  const phrases = rule.condition
    ? collectConditionPhrases(rule.condition, fieldsByPath)
    : [];
  const actions = (rule.actions ?? [])
    .map((action) => humanizeManagementAction(action))
    .filter(Boolean);
  const actionText =
    actions.length > 0 ? actions.join(", ") : "no management actions are defined";
  const enabledSuffix = rule.enabled === false ? " (currently disabled)" : "";
  const parts: PolicySummaryPart[] = [
    { kind: "plain", text: `Rule ${index + 1}${enabledSuffix}: ` },
  ];

  if (phrases.length > 0) {
    parts.push({ kind: "plain", text: "When " });
    parts.push(...joinConditionParts(phrases, "and"));
    parts.push({ kind: "plain", text: ", apply " });
  } else {
    parts.push({ kind: "plain", text: "With no entry condition, apply " });
  }

  parts.push({ kind: "plain", text: `${actionText}.` });
  return parts;
}

function listBundleSegmentRecords(policyTree: unknown): Record<string, unknown>[] {
  if (!isPlainObject(policyTree)) return [];
  const segments = policyTree.segments;
  if (!Array.isArray(segments)) return [];
  return segments.filter((segment): segment is Record<string, unknown> => isPlainObject(segment));
}

function readVersionNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function normalizeBundleGroupName(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^(entry_filter|management|segment)$/i.test(trimmed)) return null;
  const versionMatch = /^v(\d+)$/i.exec(trimmed);
  if (versionMatch) return `v${versionMatch[1]}`;
  return trimmed;
}

function resolveBundleSegmentGroupLabel(
  segment: Record<string, unknown>,
  versionsById: Map<string, ReportStrategyPolicyVersion>,
  index: number,
  bundleRoot?: Record<string, unknown>
): string | null {
  const segmentNumber = readVersionNumber(
    segment.source_policy_version_number ??
      segment.source_version_number ??
      segment.version_number
  );
  if (segmentNumber != null) return `v${segmentNumber}`;

  const sourceTitle =
    typeof segment.source_title === "string" ? normalizeBundleGroupName(segment.source_title) : null;
  if (sourceTitle) return sourceTitle;

  const segmentId =
    (typeof segment.source_policy_version_id === "string" &&
      segment.source_policy_version_id.trim()) ||
    (typeof segment.policy_version_id === "string" && segment.policy_version_id.trim()) ||
    "";
  if (segmentId) {
    const matched = versionsById.get(segmentId);
    if (matched) return versionLabel(matched);
  }

  if (bundleRoot) {
    const rootNumbers = Array.isArray(bundleRoot.source_policy_version_numbers)
      ? bundleRoot.source_policy_version_numbers
      : [];
    const rootNumber = readVersionNumber(rootNumbers[index]);
    if (rootNumber != null) return `v${rootNumber}`;

    const rootIds = Array.isArray(bundleRoot.source_policy_version_ids)
      ? bundleRoot.source_policy_version_ids
      : [];
    const rootId = typeof rootIds[index] === "string" ? rootIds[index].trim() : "";
    if (rootId) {
      const matched = versionsById.get(rootId);
      if (matched) return versionLabel(matched);
    }
  }

  return null;
}

export function serializePolicyViewSummary(document: PolicyViewSummaryDocument): string {
  return document
    .map((paragraph) => paragraph.map((part) => part.text).join(""))
    .join("\n\n");
}

/** Plain-language overview for the strategy policy view dialog. */
export function buildPolicyViewNaturalLanguageSummary(
  policyTree: unknown,
  fieldsByPath: Map<string, TradePolicyCatalogField>,
  allVersions: ReportStrategyPolicyVersion[] = []
): PolicyViewSummaryDocument {
  if (!isPlainObject(policyTree)) {
    return [plainParagraph("No policy rules are saved in this version.")];
  }

  if (isPolicyTreeV2(policyTree)) {
    const rules = policyTree.rules ?? [];
    if (rules.length === 0) {
      return [plainParagraph("This management policy does not include any rules yet.")];
    }

    // Simplify policy_tree.v2 management summaries to a single sentence that matches the
    // regime-analysis style: "This policy applies when x, y, z, apply ...".
    // This keeps the tropical-surf highlighting on the condition phrases.
    if (rules.length === 1) {
      return [buildManagementSegmentSummaryParagraph(policyTree, fieldsByPath)];
    }

    // Fallback for multi-rule trees: keep the detailed "Rule N:" formatting.
    return [
      plainParagraph(`This management policy includes ${rules.length} rule${rules.length === 1 ? "" : "s"}:`),
      ...rules.map((rule, index) => buildManagementRuleSummaryParagraph(rule, index, fieldsByPath)),
    ];
  }

  const segments = parsePolicyViewSegments(policyTree, allVersions);
  if (segments.length === 0) {
    return [plainParagraph("No policy rules are saved in this version.")];
  }

  const versionsById = new Map(allVersions.map((version) => [version.id, version]));
  const bundleRoot = isPlainObject(policyTree) ? policyTree : undefined;
  const bundleSegmentRecords = listBundleSegmentRecords(policyTree);

  if (segments.length > 1) {
    const segmentActions = bundleSegmentRecords
      .map((segment) =>
        segment.action === "veto" || segment.action === "keep_only" ? segment.action : null
      )
      .filter((a): a is TradePolicyAction => a != null);
    const combineOp =
      bundleRoot != null ? resolveBundleEditorRootOp(bundleRoot, segmentActions) : null;
    const allKeep =
      segmentActions.length > 0 && segmentActions.every((a) => a === "keep_only");
    const allVeto = segmentActions.length > 0 && segmentActions.every((a) => a === "veto");
    const managementCount = segments.filter((segment) => segment.kind === "management").length;
    const hasManagement = managementCount > 0;
    const managementOnly = hasManagement && managementCount === segments.length;
    let combinePhrase = "";
    if (managementOnly) {
      // EP combine concatenates rules; sim applies THEN of every matching WHEN (not AND across segments).
      combinePhrase =
        " Each segment is its own Exit Placement rule; every matching rule applies.";
    } else if (hasManagement && segmentActions.length > 0) {
      combinePhrase =
        " Includes Regime entry filters and Exit Placement management (matching EP rules all apply).";
    } else if (combineOp === "or" && allKeep) {
      combinePhrase = " Keep stacks combine with OR (any may match).";
    } else if (combineOp === "and" && allKeep) {
      combinePhrase = " Keep stacks combine with AND (all must match).";
    } else if (combineOp === "or" && allVeto) {
      combinePhrase = " Remove stacks combine with OR (any may remove).";
    } else if (combineOp === "and" && allVeto) {
      combinePhrase = " Remove stacks combine with AND (all must match to remove).";
    } else if (combineOp === "or") {
      combinePhrase = " Segments combine with OR.";
    } else if (combineOp === "and") {
      combinePhrase = " Segments combine with AND.";
    }
    return [
      plainParagraph(
        `This policy combines ${segments.length} ${
          hasManagement ? "policy segments" : "filter groups"
        }.${combinePhrase}`
      ),
      ...segments.map((segment, index) => {
        const groupName = bundleSegmentRecords[index]
          ? resolveBundleSegmentGroupLabel(
              bundleSegmentRecords[index],
              versionsById,
              index,
              bundleRoot
            )
          : segment.groupLabel ?? null;
        if (segment.kind === "management" && isPolicyTreeV2(segment.payload)) {
          return buildManagementSegmentSummaryParagraph(segment.payload, fieldsByPath, {
            groupIndex: index,
            groupCount: segments.length,
            groupName,
          });
        }
        return buildFilterSegmentSummaryParagraph(segment.payload, fieldsByPath, {
          groupIndex: index,
          groupCount: segments.length,
          groupName,
        });
      }),
    ];
  }

  if (segments[0].kind === "management" && isPolicyTreeV2(segments[0].payload)) {
    return [buildManagementSegmentSummaryParagraph(segments[0].payload, fieldsByPath)];
  }

  return [buildFilterSegmentSummaryParagraph(segments[0].payload, fieldsByPath)];
}

/** Plain-text serialization — useful for tests and screen readers. */
export function formatPolicyViewNaturalLanguageSummary(
  policyTree: unknown,
  fieldsByPath: Map<string, TradePolicyCatalogField>,
  allVersions: ReportStrategyPolicyVersion[] = []
): string {
  return serializePolicyViewSummary(
    buildPolicyViewNaturalLanguageSummary(policyTree, fieldsByPath, allVersions)
  );
}

function formatPolicyNodeSummaryLines(
  node: TradePolicyPolicyNode,
  fieldsByPath: Map<string, TradePolicyCatalogField>,
  depth: number
): string[] {
  const indent = "  ".repeat(depth);
  if (node.kind === "rule") {
    const f = fieldsByPath.get(node.path);
    const label = f?.label ?? node.path;
    const op = RULE_OP_SYMBOL[node.op] ?? node.op;
    const val = formatSerializedRuleValue(node);
    return [`${indent}• ${label}  ${op}  ${val}`];
  }
  const joiner = node.op === "and" ? "ALL of" : "ANY of";
  const out: string[] = [`${indent}[ ${joiner} ]`];
  for (const c of node.children) {
    out.push(...formatPolicyNodeSummaryLines(c, fieldsByPath, depth + 1));
  }
  return out;
}

function formatNodeOneLiner(
  node: TradePolicyPolicyNode,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): string {
  if (node.kind === "rule") {
    const f = fieldsByPath.get(node.path);
    const label = f?.label ?? node.path;
    const op = RULE_OP_SYMBOL[node.op] ?? node.op;
    const val = formatSerializedRuleValue(node);
    return `${label} ${op} ${val}`;
  }
  const sep = node.op === "and" ? " · " : " | ";
  const parts = node.children.map((c) => formatNodeOneLiner(c, fieldsByPath)).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  const inner = parts.join(sep);
  return node.op === "or" ? `(${inner})` : inner;
}

/**
 * Single-line policy for compact UI (hover / “full policy” for the tree view).
 */
export function formatPolicyPayloadOneLiner(
  payload: TradePolicyPolicyPayload,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): string {
  const action = formatPolicyActionLabel(payload.action) ?? "Keep";
  const tree = formatNodeOneLiner(payload.root, fieldsByPath);
  return `${action}: ${tree}`;
}

export type EditorRuleNode = {
  kind: "rule";
  id: string;
  path: string;
  op: string;
  valueEnum: string;
  valueEnumSet: string[];
  valueNumber: string;
  /** Optional numeric range inputs when op is "range". */
  valueNumberMin?: string;
  valueNumberMax?: string;
};

export type EditorGroupNode = {
  kind: "group";
  id: string;
  op: TradePolicyGroupNode["op"];
  /** Per-group simulate action (Remove vs Keep only). Falls back to toolbar default when unset. */
  action?: TradePolicyAction;
  /** Optional user-facing label for reference only (does not affect policy logic). */
  label?: string;
  /** True when the label was auto-assigned from an imported pick. */
  autoLabelFromPick?: boolean;
  /** Source pick id when this group was imported from picks. */
  sourcePickId?: string;
  /**
   * Per-segment candle TF from a combined bundle (must round-trip on simulate so BE
   * can allow finest TF vs a coarser adopted report).
   */
  candleTimeframe?: string;
  /** Bundle meta `resolved_candle_timeframe` (root shell only). */
  resolvedCandleTimeframe?: string;
  /** Bundle meta `source_candle_timeframes` (root shell only). */
  sourceCandleTimeframes?: string[];
  children: EditorNode[];
};

export type EditorNode = EditorRuleNode | EditorGroupNode;

export function genEditorId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function newEditorRule(fields: TradePolicyCatalogField[]): EditorRuleNode {
  const first = fields[0];
  const path = first?.path ?? "";
  const op = first?.allowed_ops[0] ?? "eq";
  return {
    kind: "rule",
    id: genEditorId(),
    path,
    op,
    valueEnum: "",
    valueEnumSet: [],
    valueNumber: "",
    valueNumberMin: "",
    valueNumberMax: "",
  };
}

export function newEditorGroup(
  fields: TradePolicyCatalogField[],
  defaultAction?: TradePolicyAction
): EditorGroupNode {
  return {
    kind: "group",
    id: genEditorId(),
    op: "and",
    action: defaultAction,
    label: "",
    autoLabelFromPick: false,
    sourcePickId: undefined,
    children: [newEditorRule(fields)],
  };
}

/** Empty policy shell: one root group with no nested groups or rules until the user adds them. */
export function defaultPolicyRoot(_fields: TradePolicyCatalogField[]): EditorGroupNode {
  return {
    kind: "group",
    id: genEditorId(),
    op: "and",
    label: "",
    autoLabelFromPick: false,
    sourcePickId: undefined,
    children: [],
  };
}

function combineOperandToGroupOp(raw: unknown): TradePolicyGroupOp | null {
  if (raw === "any") return "or";
  if (raw === "all") return "and";
  return null;
}

/** Read keep/veto combine operands from bundle root or `meta` (BE persist path). */
export function readBundleCombineOperands(record: Record<string, unknown>): {
  entryKeepCombine: "all" | "any" | null;
  entryVetoCombine: "all" | "any" | null;
} {
  const meta =
    record.meta && typeof record.meta === "object" && !Array.isArray(record.meta)
      ? (record.meta as Record<string, unknown>)
      : null;
  const keepRaw = meta?.entry_keep_combine ?? record.entry_keep_combine;
  const vetoRaw = meta?.entry_veto_combine ?? record.entry_veto_combine;
  return {
    entryKeepCombine: keepRaw === "all" || keepRaw === "any" ? keepRaw : null,
    entryVetoCombine: vetoRaw === "all" || vetoRaw === "any" ? vetoRaw : null,
  };
}

/**
 * Root Match op for a combined bundle in the editor.
 * Prefers persisted `meta.entry_keep_combine` / `meta.entry_veto_combine` (BE),
 * then top-level fields.
 */
export function resolveBundleEditorRootOp(
  record: Record<string, unknown>,
  segmentActions: TradePolicyAction[]
): TradePolicyGroupOp {
  const { entryKeepCombine, entryVetoCombine } = readBundleCombineOperands(record);
  const allKeep = segmentActions.length > 0 && segmentActions.every((a) => a === "keep_only");
  const allVeto = segmentActions.length > 0 && segmentActions.every((a) => a === "veto");
  if (allKeep) {
    return combineOperandToGroupOp(entryKeepCombine) ?? "and";
  }
  if (allVeto) {
    return combineOperandToGroupOp(entryVetoCombine) ?? "or";
  }
  return (
    combineOperandToGroupOp(entryKeepCombine) ??
    combineOperandToGroupOp(entryVetoCombine) ??
    "and"
  );
}

/** True when this group is a multi-policy combine shell (segments are nested groups). */
export function isPolicyCombineShellGroup(node: EditorGroupNode): boolean {
  return (
    node.children.length > 1 && node.children.every((child) => child.kind === "group")
  );
}

/**
 * Roots to show in Builder/Graph. Multi-child trees keep the shell so Match (AND/OR)
 * between combined policies is visible; a single child is shown without the empty shell.
 * When unwrapping, inherit the shell's action so Keep Only policies don't fall back to Remove.
 */
export function getPolicyEditorDisplayRoots(policyRoot: EditorNode): EditorNode[] {
  if (policyRoot.kind !== "group") return [policyRoot];
  if (policyRoot.children.length > 1) return [policyRoot];
  if (policyRoot.children.length === 1) {
    const child = policyRoot.children[0]!;
    if (
      child.kind === "group" &&
      !child.action &&
      (policyRoot.action === "veto" || policyRoot.action === "keep_only")
    ) {
      return [{ ...child, action: policyRoot.action }];
    }
    return [child];
  }
  return [];
}

/** Uniform Keep/Remove stamped on editor groups, if any; null when mixed or unset. */
export function inferUniformEditorGroupAction(node: EditorNode): TradePolicyAction | null {
  const actions: TradePolicyAction[] = [];
  const walk = (n: EditorNode) => {
    if (n.kind !== "group") return;
    if (n.action === "veto" || n.action === "keep_only") actions.push(n.action);
    for (const child of n.children) walk(child);
  };
  walk(node);
  if (actions.length === 0) return null;
  if (actions.every((a) => a === actions[0])) return actions[0]!;
  return null;
}

/** Human label for AND/OR between combined segments. */
export function formatPolicyCombineOpLabel(op: TradePolicyGroupOp): string {
  return op === "or" ? "Any of (OR)" : "All of (AND)";
}

/**
 * Label for the divider between policy-view segments.
 * Exit Placement–only stacks are independent IF/THENs (every matching rule applies) — not entry AND.
 */
export function resolvePolicyViewSegmentsCombineLabel(
  policyTree: unknown,
  segments: Array<{ kind?: string; payload: unknown }>
): string | null {
  if (segments.length < 2) return null;
  if (!policyTree || typeof policyTree !== "object" || Array.isArray(policyTree)) return null;

  const managementCount = segments.filter((segment) => segment.kind === "management").length;
  if (managementCount === segments.length) {
    return formatPolicyCombineOpLabel("or");
  }

  const record = policyTree as Record<string, unknown>;
  const actions = segments
    .map((segment) => {
      const payload = segment.payload;
      if (!payload || typeof payload !== "object") return null;
      const action = (payload as { action?: unknown }).action;
      return action === "veto" || action === "keep_only" ? action : null;
    })
    .filter((a): a is TradePolicyAction => a != null);
  const op = resolveBundleEditorRootOp(record, actions);
  return formatPolicyCombineOpLabel(op);
}

function rowStateForField(
  field: TradePolicyCatalogField | undefined
): Partial<Pick<EditorRuleNode, "op" | "valueEnum" | "valueEnumSet" | "valueNumber" | "valueNumberMin" | "valueNumberMax">> {
  if (!field) {
    return { op: "eq", valueEnum: "", valueEnumSet: [], valueNumber: "", valueNumberMin: "", valueNumberMax: "" };
  }
  const ops = field.allowed_ops;
  const op = ops[0] ?? "eq";
  return { op, valueEnum: "", valueEnumSet: [], valueNumber: "", valueNumberMin: "", valueNumberMax: "" };
}

export function editorRulePathChange(
  node: EditorRuleNode,
  path: string,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): EditorRuleNode {
  const field = fieldsByPath.get(path);
  return { ...node, path, ...rowStateForField(field) };
}

export function countEditorNodes(node: EditorNode): number {
  if (node.kind === "rule") return 1;
  return 1 + node.children.reduce((s, c) => s + countEditorNodes(c), 0);
}

export function countRuleLeaves(node: EditorNode): number {
  if (node.kind === "rule") return 1;
  return node.children.reduce((s, c) => s + countRuleLeaves(c), 0);
}

function countSiblingPolicyGroups(node: EditorGroupNode): number {
  const childGroups = node.children.filter((child) => child.kind === "group");
  if (childGroups.length > 0) return childGroups.length;
  return node.children.length > 0 ? 1 : 0;
}

/** Policy groups shown in the simulator builder (root siblings, or nested siblings under a single root group). */
export function countTopLevelSimulatorPolicyGroups(root: EditorNode): number {
  if (root.kind !== "group") return 0;
  const directGroups = root.children.filter((child): child is EditorGroupNode => child.kind === "group");
  if (directGroups.length > 1) return directGroups.length;
  if (directGroups.length === 1) {
    const nestedGroupCount = countSiblingPolicyGroups(directGroups[0]);
    return nestedGroupCount > 1 ? nestedGroupCount : 1;
  }
  return root.children.length > 0 ? 1 : 0;
}

export function clearSimulatorPolicyControlLabel(root: EditorNode): "Clear policies" | "Clear policy" {
  return countTopLevelSimulatorPolicyGroups(root) > 1 ? "Clear policies" : "Clear policy";
}

export function clearedSimulatorPolicyToastTitle(root: EditorNode): "Cleared policies" | "Cleared policy" {
  return countTopLevelSimulatorPolicyGroups(root) > 1 ? "Cleared policies" : "Cleared policy";
}

export function depthEditor(node: EditorNode): number {
  if (node.kind === "rule") return 1;
  if (node.children.length === 0) return 1;
  return 1 + Math.max(...node.children.map(depthEditor));
}

/** Tokens for enum `in` rules: draft in valueEnum while typing; otherwise valueEnumSet from policy load. */
function parseEnumInTokens(row: EditorRuleNode): string[] {
  if (row.valueEnum.trim() !== "") {
    return row.valueEnum
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (row.valueEnumSet.length > 0) {
    return row.valueEnumSet.map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

/**
 * Map user input to the catalog token (case-insensitive). If enum_values is empty, send trimmed lowercase.
 */
function resolveCatalogEnumToken(
  raw: string,
  field: TradePolicyCatalogField
): { ok: true; value: string } | { ok: false; reason: string } {
  const t = raw.trim();
  if (!t) {
    return { ok: false, reason: "empty" };
  }
  const allowed = field.enum_values;
  if (!allowed?.length) {
    return { ok: true, value: t.toLowerCase() };
  }
  const lower = t.toLowerCase();
  const hit = allowed.find((ev) => ev.toLowerCase() === lower);
  if (!hit) {
    const sample = allowed.slice(0, 5).join(", ");
    const more = allowed.length > 5 ? ", …" : "";
    return {
      ok: false,
      reason: `"${t}" is not a listed value for "${field.label}". Use one of: ${sample}${more} (matching is case-insensitive).`,
    };
  }
  return { ok: true, value: hit };
}

function serializeRule(
  row: EditorRuleNode,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): { ok: true; node: TradePolicyPolicyNode } | { ok: false; reason: string } {
  if (!row.path) {
    return { ok: false, reason: "Each rule needs a field." };
  }
  const field = fieldsByPath.get(row.path);
  if (!field) {
    return { ok: false, reason: `Unknown field: ${row.path}` };
  }
  if (row.op !== "range" && !field.allowed_ops.includes(row.op)) {
    return { ok: false, reason: `Operator "${row.op}" is not allowed for ${field.label}.` };
  }
  const op = row.op as TradePolicyRuleOp;

  if (field.value_kind === "enum") {
    if (row.op === "in") {
      const tokens = parseEnumInTokens(row);
      if (tokens.length === 0) {
        return {
          ok: false,
          reason: `Enter at least one value for "${field.label}" (comma-separated for One of…).`,
        };
      }
      const resolved: string[] = [];
      for (const tok of tokens) {
        const r = resolveCatalogEnumToken(tok, field);
        if (!r.ok) {
          if (r.reason === "empty") continue;
          return { ok: false, reason: r.reason };
        }
        resolved.push(r.value);
      }
      const seen = new Set<string>();
      const unique = resolved.filter((x) => (seen.has(x) ? false : (seen.add(x), true)));
      if (unique.length === 0) {
        return {
          ok: false,
          reason: `Enter at least one valid value for "${field.label}" (see filter catalog).`,
        };
      }
      return { ok: true, node: { kind: "rule", path: row.path, op, value: unique } };
    }
    const single = resolveCatalogEnumToken(row.valueEnum, field);
    if (!single.ok) {
      if (single.reason === "empty") {
        return { ok: false, reason: `Enter a value for "${field.label}".` };
      }
      return { ok: false, reason: single.reason };
    }
    return { ok: true, node: { kind: "rule", path: row.path, op, value: single.value } };
  }

  if (field.value_kind === "number") {
    if (row.op === "range") {
      const rawMin = (row.valueNumberMin ?? "").trim();
      const rawMax = (row.valueNumberMax ?? "").trim();
      const min = rawMin === "" ? null : parseFloat(rawMin);
      const max = rawMax === "" ? null : parseFloat(rawMax);
      if (min == null && max == null) {
        return { ok: false, reason: `Enter a Min and/or Max for "${field.label}".` };
      }
      if (min != null && !Number.isFinite(min)) return { ok: false, reason: `Enter a valid Min for "${field.label}".` };
      if (max != null && !Number.isFinite(max)) return { ok: false, reason: `Enter a valid Max for "${field.label}".` };
      const children: TradePolicyPolicyNode[] = [];
      if (min != null) {
        if (!field.allowed_ops.includes("gte")) {
          return { ok: false, reason: `Min requires ≥ for "${field.label}" (not in catalog allowed ops).` };
        }
        children.push({ kind: "rule", path: row.path, op: "gte", value: min });
      }
      if (max != null) {
        if (!field.allowed_ops.includes("lte")) {
          return { ok: false, reason: `Max requires ≤ for "${field.label}" (not in catalog allowed ops).` };
        }
        children.push({ kind: "rule", path: row.path, op: "lte", value: max });
      }
      if (children.length === 1) return { ok: true, node: children[0] };
      return { ok: true, node: { kind: "group", op: "and", children } };
    }
    const n = parseFloat(row.valueNumber);
    if (!Number.isFinite(n)) {
      return { ok: false, reason: `Enter a valid number for "${field.label}".` };
    }
    return { ok: true, node: { kind: "rule", path: row.path, op, value: n } };
  }

  if (!row.valueEnum.trim()) {
    return { ok: false, reason: `Enter a value for "${field.label}".` };
  }
  return { ok: true, node: { kind: "rule", path: row.path, op, value: row.valueEnum.trim() } };
}

function serializeNode(
  node: EditorNode,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): { ok: true; node: TradePolicyPolicyNode } | { ok: false; reason: string } {
  if (node.kind === "rule") {
    const r = serializeRule(node, fieldsByPath);
    if (!r.ok) return r;
    return { ok: true, node: r.node };
  }
  if (node.children.length === 0) {
    return { ok: false, reason: "Each group must have at least one child." };
  }
  const children: TradePolicyPolicyNode[] = [];
  for (const c of node.children) {
    const r = serializeNode(c, fieldsByPath);
    if (!r.ok) return r;
    children.push(r.node);
  }
  return { ok: true, node: { kind: "group", op: node.op, children } };
}

export function buildPolicyPayload(
  root: EditorNode,
  action: TradePolicyPolicyPayload["action"],
  fieldsByPath: Map<string, TradePolicyCatalogField>,
  maxDepth: number,
  maxNodes: number
): { ok: true; policy: TradePolicyPolicyPayload } | { ok: false; reason: string } {
  const built = buildSimulatePolicy(root, action, fieldsByPath, maxDepth, maxNodes);
  if (!built.ok) return built;
  if (isPolicyTreeBundleV1(built.policy)) {
    return {
      ok: false,
      reason: "This policy mixes Remove and Keep-only groups. Use Run simulation — it sends a policy bundle automatically.",
    };
  }
  if (!isPolicyPayloadV1(built.policy)) {
    return { ok: false, reason: "Unsupported policy schema for classic payload export." };
  }
  return { ok: true, policy: built.policy };
}

function resolveGroupAction(group: EditorGroupNode, defaultAction: TradePolicyAction): TradePolicyAction {
  return group.action ?? defaultAction;
}

/**
 * Build simulate POST body: classic v1 when a single action tree, else policy_tree.bundle.v1.
 * Combined / multi-TF policies stay as bundles so segment `candle_timeframe` and meta TF
 * stamps survive (BE allows finest TF vs a coarser report only when those stamps exist).
 */
export function buildSimulatePolicy(
  root: EditorNode,
  defaultAction: TradePolicyAction,
  fieldsByPath: Map<string, TradePolicyCatalogField>,
  maxDepth: number,
  maxNodes: number
): { ok: true; policy: TradePolicySimulatePolicy } | { ok: false; reason: string } {
  const nodes = countEditorNodes(root);
  if (nodes > maxNodes) {
    return { ok: false, reason: `Policy has ${nodes} nodes; maximum is ${maxNodes}.` };
  }
  const d = depthEditor(root);
  if (d > maxDepth) {
    return { ok: false, reason: `Policy tree depth is ${d}; maximum is ${maxDepth}.` };
  }

  const rootGroup: EditorGroupNode =
    root.kind === "group"
      ? root
      : {
          kind: "group",
          id: genEditorId(),
          op: "and",
          children: [root],
        };

  if (rootGroup.children.length === 0) {
    return { ok: false, reason: "Add at least one rule or group to the policy tree." };
  }

  const segmentActions = rootGroup.children.map((child) =>
    child.kind === "group" ? resolveGroupAction(child, defaultAction) : defaultAction
  );
  const uniqueActions = new Set(segmentActions);
  const childCandleTfs = rootGroup.children
    .map((child) =>
      child.kind === "group" && child.candleTimeframe?.trim()
        ? child.candleTimeframe.trim()
        : null
    )
    .filter((tf): tf is string => Boolean(tf));
  const useBundle =
    uniqueActions.size > 1 ||
    isPolicyCombineShellGroup(rootGroup) ||
    childCandleTfs.length > 0 ||
    Boolean(rootGroup.resolvedCandleTimeframe?.trim()) ||
    (rootGroup.sourceCandleTimeframes?.length ?? 0) > 0;

  if (useBundle) {
    const segments: PolicyTreeBundleSegmentV1[] = [];
    for (let i = 0; i < rootGroup.children.length; i++) {
      const child = rootGroup.children[i];
      const action = segmentActions[i]!;
      const ser = serializeNode(child, fieldsByPath);
      if (!ser.ok) return ser;
      const sourceTitle = child.kind === "group" ? child.label?.trim() || undefined : undefined;
      const candleTf =
        child.kind === "group" && child.candleTimeframe?.trim()
          ? child.candleTimeframe.trim()
          : undefined;
      const meta = {
        segment_kind: "entry_filter" as const,
        ...(sourceTitle ? { source_title: sourceTitle } : {}),
      };
      segments.push({
        action,
        root: ser.node,
        ...meta,
        ...(candleTf ? { candle_timeframe: candleTf } : {}),
      });
    }
    const keepCount = segmentActions.filter((a) => a === "keep_only").length;
    const vetoCount = segmentActions.filter((a) => a === "veto").length;
    // Match root op → BE combine operands. Emit whenever that stack has ≥2 segments
    // (including mixed keep+veto). Pure-keep-only used to be the only case that wrote
    // the flag; mixed Remove+Keep omitted it and BE defaulted keeps to AND → empty
    // intersection on disagreeing regime.market_condition values.
    const keepCombine = rootGroup.op === "or" ? ("any" as const) : ("all" as const);
    const vetoCombine = rootGroup.op === "or" ? ("any" as const) : ("all" as const);
    const entryKeepCombine = keepCount >= 2 ? keepCombine : undefined;
    const entryVetoCombine = vetoCount >= 2 ? vetoCombine : undefined;

    const stampedSourceTfs = [
      ...(rootGroup.sourceCandleTimeframes ?? []).map((tf) => tf.trim()).filter(Boolean),
      ...segments
        .map((seg) => (typeof seg.candle_timeframe === "string" ? seg.candle_timeframe.trim() : ""))
        .filter(Boolean),
    ];
    const uniqueSourceTfs = Array.from(new Set(stampedSourceTfs.map((tf) => normalizeCandleTimeframe(tf))));
    const resolvedFromMeta = rootGroup.resolvedCandleTimeframe?.trim() || "";
    const resolvedTf =
      resolvedFromMeta ||
      (uniqueSourceTfs.length > 0 ? resolveFinestCandleTimeframe(uniqueSourceTfs) : "");

    return {
      ok: true,
      policy: {
        schema_version: "policy_tree.bundle.v1",
        segments,
        // Dual-write: BE persist prefers meta.*; some callers still read top-level.
        ...(entryKeepCombine ? { entry_keep_combine: entryKeepCombine } : {}),
        ...(entryVetoCombine ? { entry_veto_combine: entryVetoCombine } : {}),
        meta: {
          segment_count: segments.length,
          ...(entryKeepCombine ? { entry_keep_combine: entryKeepCombine } : {}),
          ...(entryVetoCombine ? { entry_veto_combine: entryVetoCombine } : {}),
          ...(resolvedTf ? { resolved_candle_timeframe: resolvedTf } : {}),
          ...(uniqueSourceTfs.length > 0 ? { source_candle_timeframes: uniqueSourceTfs } : {}),
        },
      },
    };
  }

  const uniformAction = segmentActions[0] ?? defaultAction;
  const ser = serializeNode(rootGroup, fieldsByPath);
  if (!ser.ok) return ser;
  const policy: TradePolicyPolicyPayload = { action: uniformAction, root: ser.node };
  return { ok: true, policy };
}

export function policyNodeToEditor(n: TradePolicyPolicyNode, fieldsByPath: Map<string, TradePolicyCatalogField>): EditorNode {
  if (n.kind === "rule") {
    const field = fieldsByPath.get(n.path);
    const base: EditorRuleNode = {
      kind: "rule",
      id: genEditorId(),
      path: n.path,
      op: n.op,
      valueEnum: "",
      valueEnumSet: [],
      valueNumber: "",
    };
    if (Array.isArray(n.value)) {
      return { ...base, op: "in", valueEnum: "", valueEnumSet: [...n.value], valueNumber: "" };
    }
    if (typeof n.value === "number") {
      return { ...base, valueNumber: String(n.value), valueEnum: "", valueEnumSet: [] };
    }
    return { ...base, valueEnum: String(n.value), valueEnumSet: [], valueNumber: "" };
  }
  return {
    kind: "group",
    id: genEditorId(),
    op: n.op,
    children: n.children.map((c) => policyNodeToEditor(c, fieldsByPath)),
  };
}

function isPolicyNode(o: unknown): o is TradePolicyPolicyNode {
  if (!o || typeof o !== "object") return false;
  const k = (o as { kind?: unknown }).kind;
  if (k === "rule") {
    const r = o as TradePolicyRuleNode;
    return typeof r.path === "string" && typeof r.op === "string" && "value" in r;
  }
  if (k === "group") {
    const g = o as TradePolicyGroupNode;
    return (g.op === "and" || g.op === "or") && Array.isArray(g.children);
  }
  return false;
}

/** Hydrate a saved policy payload or root node into the simulator editor tree. */
export function parsePolicyTreeToEditorRoot(
  raw: unknown,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): EditorNode | null {
  if (raw == null || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const segmentsRaw = record.segments;
  const segments = Array.isArray(segmentsRaw)
    ? segmentsRaw.filter((s): s is Record<string, unknown> => isPlainObject(s))
    : [];
  const isBundle =
    String(record.schema_version ?? "") === "policy_tree.bundle.v1" ||
    (segments.length > 0 && !("root" in record));
  if (isBundle && segments.length > 0) {
    const children: EditorNode[] = [];
    const segmentActions: TradePolicyAction[] = [];
    const meta =
      record.meta && typeof record.meta === "object" && !Array.isArray(record.meta)
        ? (record.meta as Record<string, unknown>)
        : null;
    const resolvedCandleTimeframe =
      (typeof meta?.resolved_candle_timeframe === "string" && meta.resolved_candle_timeframe.trim()) ||
      (typeof record.resolved_candle_timeframe === "string" &&
        String(record.resolved_candle_timeframe).trim()) ||
      undefined;
    const sourceCandleTimeframesRaw = meta?.source_candle_timeframes ?? record.source_candle_timeframes;
    const sourceCandleTimeframes = Array.isArray(sourceCandleTimeframesRaw)
      ? sourceCandleTimeframesRaw
          .map((tf) => (typeof tf === "string" ? tf.trim() : ""))
          .filter(Boolean)
      : undefined;
    segments.forEach((segment, index) => {
      const kind =
        typeof segment.segment_kind === "string"
          ? segment.segment_kind.trim().toLowerCase()
          : "";
      // Exit Placement segments are shown via ExitPlacementPolicyTreeEditor, not the v1 tree.
      if (
        kind === "management" ||
        (Array.isArray(segment.rules) && segment.rules.length > 0) ||
        (segment.rule != null && typeof segment.rule === "object")
      ) {
        return;
      }
      const nested = parsePolicyTreeToEditorRoot(segment, fieldsByPath);
      if (!nested) return;
      // Empty roots from EP stubs (no action) — skip rather than render a blank group.
      if (
        nested.kind === "group" &&
        nested.children.length === 0 &&
        segment.action !== "veto" &&
        segment.action !== "keep_only"
      ) {
        return;
      }
      const action =
        segment.action === "veto" || segment.action === "keep_only"
          ? segment.action
          : undefined;
      if (action) segmentActions.push(action);
      const label =
        (typeof segment.label === "string" && segment.label.trim()) ||
        (typeof segment.title === "string" && segment.title.trim()) ||
        `Segment ${index + 1}`;
      const candleTimeframe =
        typeof segment.candle_timeframe === "string" && segment.candle_timeframe.trim()
          ? segment.candle_timeframe.trim()
          : undefined;
      if (nested.kind === "group") {
        children.push({
          ...nested,
          ...(action ? { action } : {}),
          ...(candleTimeframe ? { candleTimeframe } : {}),
          label: nested.label?.trim() || label,
        });
      } else {
        children.push({
          ...defaultPolicyRoot([]),
          ...(action ? { action } : {}),
          ...(candleTimeframe ? { candleTimeframe } : {}),
          label,
          children: [nested],
        });
      }
    });
    if (children.length === 0) return null;
    const rootOp = resolveBundleEditorRootOp(record, segmentActions);
    return {
      ...defaultPolicyRoot([]),
      op: rootOp,
      label: children.length > 1 ? "Combined policies" : "",
      ...(resolvedCandleTimeframe ? { resolvedCandleTimeframe } : {}),
      ...(sourceCandleTimeframes && sourceCandleTimeframes.length > 0
        ? { sourceCandleTimeframes }
        : {}),
      children,
    };
  }

  let rootCandidate: unknown = "root" in record ? record.root : raw;
  if (isPlainObject(rootCandidate)) {
    rootCandidate = unwrapRedundantPolicyGroup(rootCandidate);
  }
  if (!isPolicyNode(rootCandidate)) return null;
  const editor = policyNodeToEditor(rootCandidate, fieldsByPath);
  const action =
    record.action === "veto" || record.action === "keep_only" ? record.action : undefined;
  if (editor.kind === "group") {
    if (!action) return editor;
    // Stamp Keep/Remove onto the group and any unlabeled nested groups so the builder
    // Action dropdown matches the policy (avoids defaulting nested groups to Remove).
    const stamp = (n: EditorNode): EditorNode => {
      if (n.kind !== "group") return n;
      return {
        ...n,
        action: n.action ?? action,
        children: n.children.map(stamp),
      };
    };
    return stamp({ ...editor, action }) as EditorGroupNode;
  }
  return {
    ...defaultPolicyRoot([]),
    ...(action ? { action } : {}),
    children: [editor],
  };
}

/** Nodes to render in read-only policy view — keeps rules nested under their group (unlike the editor shell). */
export function getPolicyViewDisplayNodes(policyRoot: EditorNode): EditorNode[] {
  if (policyRoot.kind === "rule") return [policyRoot];
  if (policyRoot.children.length === 0) return [];

  const hasTopLevelRules = policyRoot.children.some((child) => child.kind === "rule");
  if (hasTopLevelRules) {
    return [policyRoot];
  }

  return policyRoot.children;
}

/** WHEN display nodes per policy_tree.v2 rule (Builder / Graph). */
export function policyTreeV2RuleDisplayNodes(
  policy: PolicyTreeV2,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): EditorNode[][] {
  return (policy.rules ?? []).map((rule) => {
    const root = parsePolicyTreeToEditorRoot({ root: rule.condition }, fieldsByPath);
    if (!root) return [];
    return getPolicyViewDisplayNodes(root);
  });
}

/** All accordion node ids across policy_tree.v2 rule conditions (for Hide all / Show all). */
export function policyTreeV2ConditionNodeIds(
  policy: PolicyTreeV2,
  fieldsByPath: Map<string, TradePolicyCatalogField>
): string[] {
  const out: string[] = [];
  const walk = (node: EditorNode) => {
    out.push(node.id);
    if (node.kind === "group") {
      for (const child of node.children) walk(child);
    }
  };
  for (const nodes of policyTreeV2RuleDisplayNodes(policy, fieldsByPath)) {
    for (const node of nodes) walk(node);
  }
  return out;
}

function resolveSourcePolicyVersionForSegment(
  segment: Record<string, unknown>,
  versionsById: Map<string, ReportStrategyPolicyVersion>,
  index: number,
  bundleRoot?: Record<string, unknown>
): ReportStrategyPolicyVersion | null {
  const segmentId =
    (typeof segment.source_policy_version_id === "string" &&
      segment.source_policy_version_id.trim()) ||
    (typeof segment.policy_version_id === "string" && segment.policy_version_id.trim()) ||
    "";
  if (segmentId) {
    const matched = versionsById.get(segmentId);
    if (matched) return matched;
  }

  const segmentNumber = readVersionNumber(
    segment.source_policy_version_number ??
      segment.source_version_number ??
      segment.version_number
  );
  if (segmentNumber != null) {
    for (const version of Array.from(versionsById.values())) {
      if (version.version_number === segmentNumber) return version;
    }
  }

  const title =
    typeof segment.source_title === "string" ? segment.source_title.trim() : "";
  const titleVersion = /^v(\d+)$/i.exec(title);
  if (titleVersion) {
    const fromTitle = Number(titleVersion[1]);
    for (const version of Array.from(versionsById.values())) {
      if (version.version_number === fromTitle) return version;
    }
  }

  if (bundleRoot) {
    const rootIds = Array.isArray(bundleRoot.source_policy_version_ids)
      ? bundleRoot.source_policy_version_ids
      : [];
    const rootId = typeof rootIds[index] === "string" ? String(rootIds[index]).trim() : "";
    if (rootId) {
      const matched = versionsById.get(rootId);
      if (matched) return matched;
    }
    const rootNumbers = Array.isArray(bundleRoot.source_policy_version_numbers)
      ? bundleRoot.source_policy_version_numbers
      : [];
    const rootNumber = readVersionNumber(rootNumbers[index]);
    if (rootNumber != null) {
      for (const version of Array.from(versionsById.values())) {
        if (version.version_number === rootNumber) return version;
      }
    }
  }

  return null;
}

/** Coerce an embedded or source-linked Exit Placement tree from a bundle segment. */
export function coerceManagementPolicyFromBundleSegment(
  segment: Record<string, unknown>,
  sourceVersion: ReportStrategyPolicyVersion | null = null
): PolicyTreeV2 | null {
  if (isPolicyTreeV2(segment)) return segment;

  const nested = segment.policy_tree ?? segment.policy;
  if (isPolicyTreeV2(nested)) return nested;

  if (Array.isArray(segment.rules) && segment.rules.length > 0) {
    const candidate = { schema_version: "policy_tree.v2", rules: segment.rules };
    if (isPolicyTreeV2(candidate)) return candidate;
  }

  // BE combine / EP bundles often store one rule per segment as `rule` (singular).
  const singular = segment.rule;
  if (singular && typeof singular === "object" && !Array.isArray(singular)) {
    const ruleRec = singular as Record<string, unknown>;
    if (
      ruleRec.condition != null &&
      typeof ruleRec.condition === "object" &&
      Array.isArray(ruleRec.actions)
    ) {
      const candidate: PolicyTreeV2 = {
        schema_version: "policy_tree.v2",
        rules: [
          {
            id: typeof ruleRec.id === "string" ? ruleRec.id : undefined,
            enabled: ruleRec.enabled === false ? false : true,
            condition: ruleRec.condition as PolicyTreeV2Rule["condition"],
            actions: ruleRec.actions as PolicyTreeV2Rule["actions"],
            ...(ruleRec.meta && typeof ruleRec.meta === "object"
              ? { meta: ruleRec.meta as PolicyTreeV2Rule["meta"] }
              : {}),
          },
        ],
      };
      if (isPolicyTreeV2(candidate)) return candidate;
    }
  }

  // Single-rule shape: condition + actions on the segment itself.
  if (
    segment.condition != null &&
    typeof segment.condition === "object" &&
    Array.isArray(segment.actions)
  ) {
    const candidate: PolicyTreeV2 = {
      schema_version: "policy_tree.v2",
      rules: [
        {
          id: typeof segment.id === "string" ? segment.id : undefined,
          enabled: segment.enabled === false ? false : true,
          condition: segment.condition as PolicyTreeV2Rule["condition"],
          actions: segment.actions as PolicyTreeV2Rule["actions"],
        },
      ],
    };
    if (isPolicyTreeV2(candidate)) return candidate;
  }

  // WHEN in root + THEN actions on the segment (some combine payloads).
  if (
    segment.root != null &&
    typeof segment.root === "object" &&
    Array.isArray(segment.actions) &&
    segment.actions.length > 0
  ) {
    const candidate: PolicyTreeV2 = {
      schema_version: "policy_tree.v2",
      rules: [
        {
          condition: segment.root as PolicyTreeV2Rule["condition"],
          actions: segment.actions as PolicyTreeV2Rule["actions"],
        },
      ],
    };
    if (isPolicyTreeV2(candidate)) return candidate;
  }

  if (sourceVersion) {
    if (isPolicyTreeV2(sourceVersion.policy_tree)) return sourceVersion.policy_tree;
    if (sourceVersion.policy_schema_version === "policy_tree.v2") {
      const tree = sourceVersion.policy_tree;
      if (isPlainObject(tree) && Array.isArray(tree.rules)) {
        const candidate = { schema_version: "policy_tree.v2", rules: tree.rules };
        if (isPolicyTreeV2(candidate)) return candidate;
      }
    }
  }

  return null;
}

function isEntryFilterAction(action: unknown): boolean {
  return action === "veto" || action === "keep_only";
}

function isEmptyPolicyRoot(root: unknown): boolean {
  if (root == null) return true;
  if (!isPlainObject(root)) return false;
  if (root.kind === "rule") return false;
  if (root.kind === "group") {
    return !Array.isArray(root.children) || root.children.length === 0;
  }
  return false;
}

function isManagementBundleSegment(
  segment: Record<string, unknown>,
  managementPolicy: PolicyTreeV2 | null,
  sourceVersion: ReportStrategyPolicyVersion | null
): boolean {
  const kind =
    typeof segment.segment_kind === "string" ? segment.segment_kind.trim().toLowerCase() : "";
  if (kind === "management") return true;
  if (kind === "entry_filter") return false;

  const sourceIsV2 =
    Boolean(sourceVersion && isPolicyTreeV2(sourceVersion.policy_tree)) ||
    sourceVersion?.policy_schema_version === "policy_tree.v2";

  // Inline v2 / condition+actions on the segment.
  if (managementPolicy != null && !isEntryFilterAction(segment.action)) return true;

  // BE combine: one EP rule per segment as `rule` (singular).
  if (segment.rule && typeof segment.rule === "object" && !Array.isArray(segment.rule)) {
    return true;
  }

  // Combine often stores EP stubs as empty roots; hydrate from the source v2 version.
  if (sourceIsV2 && isEmptyPolicyRoot(segment.root)) return true;

  if (managementPolicy != null && isEmptyPolicyRoot(segment.root)) return true;

  return false;
}

function managementPolicyActionSubtitle(policy: PolicyTreeV2): string | undefined {
  const parts = (policy.rules ?? [])
    .flatMap((rule) => (rule.actions ?? []).map((action) => humanizeManagementAction(action)))
    .filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

export type HydratePolicyTreeManagementOptions = {
  /** Canvas / source map: policy version id → candle TF (e.g. EP report TF). */
  candleTimeframeByVersionId?: Record<string, string>;
  /** Fallback management TF (node meta / Run hint) when segments are unstamped. */
  managementCandleTimeframeHint?: string | null;
};

function resolveManagementSegmentCandleTimeframe(input: {
  segment: Record<string, unknown>;
  sourceVersion: ReportStrategyPolicyVersion | null;
  candleTimeframeByVersionId?: Record<string, string>;
  managementCandleTimeframeHint?: string | null;
}): string | undefined {
  const existing =
    typeof input.segment.candle_timeframe === "string" && input.segment.candle_timeframe.trim()
      ? input.segment.candle_timeframe.trim()
      : "";
  if (existing) return existing;

  const fromSourceTree = candleTimeframeFromPolicyTree(input.sourceVersion?.policy_tree);
  if (fromSourceTree) return normalizeCandleTimeframe(fromSourceTree);

  const sourceId = input.sourceVersion?.id?.trim() || "";
  const fromMap =
    sourceId && input.candleTimeframeByVersionId
      ? input.candleTimeframeByVersionId[sourceId]?.trim() || ""
      : "";
  if (fromMap) return normalizeCandleTimeframe(fromMap);

  const hint = input.managementCandleTimeframeHint?.trim() || "";
  return hint ? normalizeCandleTimeframe(hint) : undefined;
}

/**
 * Fill Exit Placement stubs in a combined bundle from source policy versions
 * (combine often stores empty `root` + source_policy_version_* only).
 * Also stamps management `candle_timeframe` when recoverable (dual-TF RA+EP).
 */
export function hydratePolicyTreeManagementSegments(
  policy: unknown,
  versions: ReportStrategyPolicyVersion[] = [],
  options: HydratePolicyTreeManagementOptions = {}
): unknown {
  if (!isPolicyTreeBundleV1(policy)) return policy;
  const versionsById = new Map(versions.map((version) => [version.id, version]));
  let managementTf: string | undefined;
  const segments = policy.segments.map((segment, index) => {
    const record = segment as unknown as Record<string, unknown>;
    const sourceVersion = resolveSourcePolicyVersionForSegment(
      record,
      versionsById,
      index,
      policy as unknown as Record<string, unknown>
    );
    const managementPolicy = coerceManagementPolicyFromBundleSegment(record, sourceVersion);
    if (!isManagementBundleSegment(record, managementPolicy, sourceVersion)) {
      return segment;
    }
    const policyV2: PolicyTreeV2 = managementPolicy ?? {
      schema_version: "policy_tree.v2",
      rules: [],
    };
    const firstCondition = policyV2.rules?.[0]?.condition;
    const candleTf = resolveManagementSegmentCandleTimeframe({
      segment: record,
      sourceVersion,
      candleTimeframeByVersionId: options.candleTimeframeByVersionId,
      managementCandleTimeframeHint: options.managementCandleTimeframeHint,
    });
    if (candleTf && !managementTf) managementTf = candleTf;
    return {
      ...segment,
      segment_kind: "management" as const,
      rules: policyV2.rules ?? [],
      ...(isEmptyPolicyRoot(segment.root) && firstCondition
        ? { root: firstCondition }
        : {}),
      ...(candleTf ? { candle_timeframe: candleTf } : {}),
    };
  });
  const prevMeta =
    policy.meta && typeof policy.meta === "object" ? { ...policy.meta } : {};
  const metaManagement =
    typeof prevMeta.management_candle_timeframe === "string" &&
    prevMeta.management_candle_timeframe.trim()
      ? prevMeta.management_candle_timeframe.trim()
      : managementTf || options.managementCandleTimeframeHint?.trim() || "";
  return {
    ...policy,
    segments,
    ...(metaManagement
      ? {
          meta: {
            ...prevMeta,
            management_candle_timeframe: normalizeCandleTimeframe(metaManagement),
          },
        }
      : {}),
  };
}

/** All Exit Placement rules embedded in a (possibly hydrated) bundle. */
export function extractBundleManagementPolicyTree(
  policy: unknown,
  versions: ReportStrategyPolicyVersion[] = []
): PolicyTreeV2 | null {
  if (isPolicyTreeV2(policy)) {
    return (policy.rules?.length ?? 0) > 0 ? policy : null;
  }
  const hydrated = hydratePolicyTreeManagementSegments(policy, versions);
  if (!isPolicyTreeBundleV1(hydrated)) return null;
  const rules: PolicyTreeV2Rule[] = [];
  for (const segment of hydrated.segments) {
    const record = segment as unknown as Record<string, unknown>;
    const managementPolicy = coerceManagementPolicyFromBundleSegment(record, null);
    if (!managementPolicy?.rules?.length) continue;
    if (
      !isManagementBundleSegment(record, managementPolicy, null) &&
      record.segment_kind !== "management"
    ) {
      continue;
    }
    rules.push(...managementPolicy.rules);
  }
  if (rules.length === 0) return null;
  return { schema_version: "policy_tree.v2", rules };
}

function segmentLooksLikeManagement(segment: PolicyTreeBundleSegmentV1): boolean {
  const record = segment as unknown as Record<string, unknown>;
  if (record.segment_kind === "management") return true;
  if (Array.isArray(record.rules) && (record.rules as unknown[]).length > 0) return true;
  if (record.rule && typeof record.rule === "object" && !Array.isArray(record.rule)) return true;
  return false;
}

/**
 * Keep Exit Placement management segments from a canvas prefill bundle when the
 * classic editor rebuilds entry-only trees (management stubs are not editable there).
 */
export function mergePrefillManagementIntoBuiltPolicy(
  built: TradePolicySimulatePolicy,
  prefill: unknown
): TradePolicySimulatePolicy {
  if (!isPolicyTreeBundleV1(prefill)) return built;
  const managementSegments = prefill.segments.filter(segmentLooksLikeManagement);
  if (managementSegments.length === 0) return built;

  if (isPolicyTreeBundleV1(built)) {
    const entrySegments = built.segments.filter((segment) => !segmentLooksLikeManagement(segment));
    return {
      ...prefill,
      ...built,
      schema_version: "policy_tree.bundle.v1",
      segments: [...entrySegments, ...managementSegments],
      meta: built.meta ?? prefill.meta,
      entry_keep_combine: built.entry_keep_combine ?? prefill.entry_keep_combine,
      entry_veto_combine: built.entry_veto_combine ?? prefill.entry_veto_combine,
    };
  }

  if (isPolicyPayloadV1(built)) {
    return {
      schema_version: "policy_tree.bundle.v1",
      segments: [
        {
          action: built.action,
          root: built.root,
          segment_kind: "entry_filter",
        },
        ...managementSegments,
      ],
      meta: prefill.meta,
      entry_keep_combine: prefill.entry_keep_combine,
      entry_veto_combine: prefill.entry_veto_combine,
    };
  }

  return built;
}

function buildManagementSegmentSummaryParagraph(
  policy: PolicyTreeV2,
  fieldsByPath: Map<string, TradePolicyCatalogField>,
  opts?: { groupIndex?: number; groupCount?: number; groupName?: string | null }
): PolicySummaryParagraph {
  const isMultiGroup =
    typeof opts?.groupCount === "number" && opts.groupCount > 1;
  const parts: PolicySummaryPart[] = [];

  if (isMultiGroup && typeof opts?.groupIndex === "number") {
    const groupName = opts.groupName?.trim() ?? "";
    const label = groupName
      ? `Group ${opts.groupIndex + 1} (${groupName}): `
      : `Group ${opts.groupIndex + 1}: `;
    parts.push({ kind: "plain", text: label });
  }

  const rules = policy.rules ?? [];
  if (rules.length === 0) {
    parts.push({
      kind: "plain",
      text: isMultiGroup
        ? "Exit placement rules are missing for this segment."
        : "This management policy does not include any rules yet.",
    });
    return parts;
  }

  if (rules.length === 1) {
    const rule = rules[0];
    const phrases = rule.condition ? collectConditionPhrases(rule.condition, fieldsByPath) : [];
    const actions = (rule.actions ?? [])
      .map((action) => humanizeManagementAction(action))
      .filter(Boolean);
    const actionText =
      actions.length > 0 ? actions.join(", ") : "no management actions are defined";
    const disabledSuffix = rule.enabled === false ? " (currently disabled)" : "";

    if (!isMultiGroup) {
      parts.push({ kind: "plain", text: "This management policy applies when " });
    } else {
      parts.push({ kind: "plain", text: "Applies when " });
    }

    if (phrases.length > 0) {
      parts.push(...joinConditionParts(phrases, "and"));
      parts.push({ kind: "plain", text: `, apply ${actionText}.${disabledSuffix}` });
    } else {
      parts.push({
        kind: "plain",
        text: `no entry conditions are defined yet, apply ${actionText}.${disabledSuffix}`,
      });
    }
    return parts;
  }

  parts.push({
    kind: "plain",
    text: `Exit placement with ${rules.length} rule${rules.length === 1 ? "" : "s"}.`,
  });
  return parts;
}

export type PolicyViewSegment = {
  key: string;
  title: string;
  subtitle?: string;
  /** Source policy label for top-level groups in combined bundles (e.g. v17). */
  groupLabel?: string;
  /** Regime keep/remove vs Exit Placement management. */
  kind?: "entry" | "management";
  payload: unknown;
};

/** Split bundle or single-tree policy payloads into viewable segments. */
export function parsePolicyViewSegments(
  policyTree: unknown,
  allVersions: ReportStrategyPolicyVersion[] = []
): PolicyViewSegment[] {
  if (!isPlainObject(policyTree)) return [];
  const segmentsRaw = policyTree.segments;
  const segments = Array.isArray(segmentsRaw)
    ? segmentsRaw.filter((s): s is Record<string, unknown> => isPlainObject(s))
    : [];
  const isBundle =
    String(policyTree.schema_version ?? "") === "policy_tree.bundle.v1" || segments.length > 0;

  if (isBundle && segments.length > 0) {
    const versionsById = new Map(allVersions.map((version) => [version.id, version]));
    return segments.map((segment, index) => {
      const groupLabel =
        resolveBundleSegmentGroupLabel(segment, versionsById, index, policyTree) ?? undefined;
      const candleTf =
        typeof segment.candle_timeframe === "string" ? segment.candle_timeframe.trim() : "";
      const sourceVersion = resolveSourcePolicyVersionForSegment(
        segment,
        versionsById,
        index,
        policyTree
      );
      const managementPolicy = coerceManagementPolicyFromBundleSegment(segment, sourceVersion);
      const isManagement = isManagementBundleSegment(segment, managementPolicy, sourceVersion);

      if (isManagement) {
        const policy: PolicyTreeV2 = managementPolicy ?? {
          schema_version: "policy_tree.v2",
          rules: [],
        };
        const actionSummary = managementPolicyActionSubtitle(policy);
        const subtitleParts = [
          actionSummary || "Exit placement",
          candleTf ? `Candle ${candleTf}` : null,
        ].filter(Boolean);
        return {
          key: `segment-${index}`,
          title: groupLabel ?? "",
          subtitle: subtitleParts.length > 0 ? subtitleParts.join(" · ") : undefined,
          groupLabel,
          kind: "management" as const,
          payload: policy,
        };
      }

      const action =
        typeof segment.action === "string" ? segment.action.trim().replace(/_/g, " ") : "";
      const subtitleParts = [
        action ? action : null,
        candleTf ? `Candle ${candleTf}` : null,
      ].filter(Boolean);
      return {
        key: `segment-${index}`,
        title: groupLabel ?? "",
        subtitle: subtitleParts.length > 0 ? subtitleParts.join(" · ") : undefined,
        groupLabel,
        kind: "entry" as const,
        payload: { root: segment.root, action: segment.action },
      };
    });
  }

  if ("root" in policyTree || policyTree.kind === "group" || policyTree.kind === "rule") {
    return [
      {
        key: "policy-root",
        title: "",
        kind: "entry",
        payload: policyTree,
      },
    ];
  }

  if (isPolicyTreeV2(policyTree)) {
    return [
      {
        key: "policy-v2",
        title: "",
        kind: "management",
        payload: policyTree,
      },
    ];
  }

  return [];
}

/** Load template from catalog policy_tree.example (root-only or { action, root }). */
export function exampleToEditorRoot(
  example: unknown,
  fieldsByPath: Map<string, TradePolicyCatalogField>,
  fallbackFields: TradePolicyCatalogField[]
): EditorNode | null {
  if (example == null) return null;
  let rootRaw: unknown = example;
  if (typeof example === "object" && example !== null && "root" in example) {
    rootRaw = (example as { root: unknown }).root;
  }
  if (!isPolicyNode(rootRaw)) return null;
  return policyNodeToEditor(rootRaw, fieldsByPath);
}

export function updateNodeById(node: EditorNode, id: string, fn: (n: EditorNode) => EditorNode): EditorNode {
  if (node.id === id) return fn(node);
  if (node.kind === "group") {
    return { ...node, children: node.children.map((c) => updateNodeById(c, id, fn)) };
  }
  return node;
}

export function removeNodeById(node: EditorNode, id: string): EditorNode | null {
  if (node.kind === "rule") {
    return node.id === id ? null : node;
  }
  if (node.id === id) {
    return null;
  }
  const nextChildren = node.children
    .map((c) => removeNodeById(c, id))
    .filter((c): c is EditorNode => c != null);
  if (nextChildren.length === 0) return null;
  return { ...node, children: nextChildren };
}

export function addChildToGroup(node: EditorNode, groupId: string, child: EditorNode): EditorNode {
  if (node.kind === "group") {
    if (node.id === groupId) {
      return { ...node, children: [...node.children, child] };
    }
    return { ...node, children: node.children.map((c) => addChildToGroup(c, groupId, child)) };
  }
  return node;
}

export function findNodeById(node: EditorNode, id: string): EditorNode | null {
  if (node.id === id) return node;
  if (node.kind === "group") {
    for (const c of node.children) {
      const f = findNodeById(c, id);
      if (f) return f;
    }
  }
  return null;
}
